import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Video, 
  Plus, 
  Upload, 
  Play,
  Pause,
  CheckCircle2, 
  XCircle,
  Clock,
  Eye,
  Download,
  Search,
  Filter,
  Calendar,
  User,
  MapPin,
  FileVideo,
  Camera,
  RotateCcw,
  FastForward,
  Volume2,
  VolumeX,
  Maximize,
  Edit3,
  Trash2,
  AlertTriangle,
  Shield,
  Award,
  Activity,
  BarChart3,
  Settings
} from 'lucide-react';
import UniversalComments from './UniversalComments';

const API = `${BACKEND_URL}/api`;

const VideoVerification = ({ projectId, user }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvingVideo, setApprovingVideo] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    verification_type: 'project_progress',
    priority: 'medium',
    location: '',
    tags: '',
    video_url: ''
  });

  useEffect(() => {
    fetchVideos();
  }, [projectId]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadVideo = async (e) => {
    e.preventDefault();
    try {
      let videoUrl = videoForm.video_url;
      
      // If user selected a file, convert to base64
      if (videoFile) {
        // Check file size (limit to 10MB due to MongoDB 16MB document limit)
        // Base64 encoding increases size by ~33%, plus other document fields
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (videoFile.size > maxSize) {
          toast.error('Video file is too large. Maximum size is 10MB. Please upload to YouTube/Vimeo and use the video URL instead.');
          return;
        }
        
        toast.info('Uploading video file... This may take a moment.');
        try {
          videoUrl = await convertFileToBase64(videoFile);
        } catch (conversionError) {
          console.error('Error converting video:', conversionError);
          toast.error('Failed to process video file. Please try a smaller file or use a video URL.');
          return;
        }
      }
      
      if (!videoUrl) {
        toast.error('Please either select a video file or provide a video URL');
        return;
      }
      
      const videoData = {
        ...videoForm,
        video_url: videoUrl,
        video_file_name: videoFile ? videoFile.name : null,
        video_file_size: videoFile ? videoFile.size : null,
        uploaded_by: user.username,
        uploaded_by_name: user.organization_name || user.username,
        tags: (videoForm.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      const response = await axios.post(`${API}/projects/${projectId}/videos`, videoData);
      setVideos([...videos, response.data]);
      setShowUploadDialog(false);
      resetVideoForm();
      toast.success('Video uploaded successfully!');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const resetVideoForm = () => {
    setVideoForm({
      title: '',
      description: '',
      verification_type: 'project_progress',
      priority: 'medium',
      location: '',
      tags: '',
      video_url: ''
    });
    setVideoFile(null);
    setEditingVideoId(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 500MB)
      if (file.size > 500 * 1024 * 1024) {
        toast.error('File size must be less than 500MB');
        return;
      }
      setVideoFile(file);
      toast.success(`Selected: ${file.name}`);
    }
  };

  const handleEdit = (video) => {
    setEditingVideoId(video.id);
    setVideoForm({
      title: video.title || '',
      description: video.description || '',
      verification_type: video.verification_type || 'project_progress',
      priority: video.priority || 'medium',
      location: video.location || '',
      tags: Array.isArray(video.tags) ? video.tags.join(', ') : '',
      video_url: video.video_url || ''
    });
    setShowUploadDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      let videoUrl = videoForm.video_url;
      
      // If user selected a new file, convert to base64
      if (videoFile) {
        // Check file size (limit to 10MB due to MongoDB 16MB document limit)
        // Base64 encoding increases size by ~33%, plus other document fields
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (videoFile.size > maxSize) {
          toast.error('Video file is too large. Maximum size is 10MB. Please upload to YouTube/Vimeo and use the video URL instead.');
          return;
        }
        
        toast.info('Uploading new video file... This may take a moment.');
        try {
          videoUrl = await convertFileToBase64(videoFile);
        } catch (conversionError) {
          console.error('Error converting video:', conversionError);
          toast.error('Failed to process video file. Please try a smaller file or use a video URL.');
          return;
        }
      }
      
      const updateData = {
        ...videoForm,
        video_url: videoUrl,
        video_file_name: videoFile ? videoFile.name : videoForm.video_file_name,
        video_file_size: videoFile ? videoFile.size : videoForm.video_file_size,
        tags: (videoForm.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      await axios.put(`${API}/projects/${projectId}/videos/${editingVideoId}`, updateData);
      
      const statusMsg = user.role === 'social_actor' ? 
        'Video edit submitted for approval!' : 
        'Video updated successfully!';
      toast.success(statusMsg);
      
      setShowUploadDialog(false);
      resetVideoForm();
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update video');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/videos/${videoId}`);
      
      const statusMsg = user.role === 'social_actor' ? 
        'Video deletion submitted for approval!' : 
        'Video deleted successfully!';
      toast.success(statusMsg);
      
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete video');
    }
  };

  const openApprovalDialog = (video) => {
    setApprovingVideo(video);
    setApprovalNotes('');
    setShowApprovalDialog(true);
  };

  const handleApprove = async (approved) => {
    if (!approvingVideo) return;
    
    try {
      const endpoint = `${API}/projects/${projectId}/videos/${approvingVideo.id}/approve-edit`;
      
      await axios.post(endpoint, {
        approved: approved,
        notes: approvalNotes
      });
      
      toast.success(approved ? 'Changes approved successfully!' : 'Changes rejected');
      setShowApprovalDialog(false);
      setApprovingVideo(null);
      setApprovalNotes('');
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const handleApproveDeletion = async (video, approved) => {
    try {
      await axios.post(`${API}/projects/${projectId}/videos/${video.id}/approve-deletion`, {
        approved: approved,
        notes: approvalNotes || ''
      });
      
      toast.success(approved ? 'Deletion approved' : 'Deletion rejected');
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process deletion approval');
    }
  };

  const getEditStatusBadge = (video) => {
    if (video.edit_status === 'pending_approval') {
      return <Badge className="bg-yellow-100 text-yellow-800 ml-2">Pending Approval</Badge>;
    }
    if (video.edit_status === 'pending_deletion') {
      return <Badge className="bg-red-100 text-red-800 ml-2">Deletion Requested</Badge>;
    }
    return null;
  };

  const handlePlayVideo = (video) => {
    if (!video.video_url) {
      toast.error('No video URL available');
      return;
    }
    setPlayingVideo(video);
    setShowVideoPlayer(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <Video className="h-5 w-5 text-purple-600" />
            <span>Video Verification System</span>
          </h3>
          <p className="text-sm text-gray-600">Upload, review, and manage verification videos for physical objects</p>
        </div>
        {user.role !== 'investor' && (
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2">
                <Upload className="h-4 w-4" />
                <span>Upload Video</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl p-0">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle>{editingVideoId ? 'Edit Video' : 'Upload Video for Verification'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={editingVideoId ? handleUpdate : handleUploadVideo}>
                <div className="max-h-[60vh] overflow-y-auto px-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="video_title">Video Title *</Label>
                  <Input
                    id="video_title"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter video title"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="video_description">Description</Label>
                  <Textarea
                    id="video_description"
                    value={videoForm.description}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this video shows"
                    className="min-h-[100px]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="verification_type">Verification Type</Label>
                    <Select value={videoForm.verification_type} onValueChange={(value) => setVideoForm(prev => ({ ...prev, verification_type: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equipment_installation">Equipment Installation</SelectItem>
                        <SelectItem value="training_session">Training Session</SelectItem>
                        <SelectItem value="project_progress">Project Progress</SelectItem>
                        <SelectItem value="beneficiary_testimonial">Beneficiary Testimonial</SelectItem>
                        <SelectItem value="site_inspection">Site Inspection</SelectItem>
                        <SelectItem value="completion_verification">Completion Verification</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority Level</Label>
                    <Select value={videoForm.priority} onValueChange={(value) => setVideoForm(prev => ({ ...prev, priority: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="location">Location (Optional)</Label>
                  <Input
                    id="location"
                    value={videoForm.location}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Where was this video recorded?"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    value={videoForm.tags}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="e.g., equipment, training, progress"
                  />
                </div>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileVideo className="h-12 w-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-600 mb-2">Drop your video here or click to browse</p>
                  <p className="text-xs text-gray-500">Supported formats: MP4, MOV, AVI (Max size: 10MB)</p>
                  <p className="text-xs text-orange-700 mt-1 font-semibold">⚠️ For larger videos, upload to YouTube/Vimeo and use URL below</p>
                  {videoFile && (
                    <p className="text-sm text-green-700 mt-2">Selected: {videoFile.name}</p>
                  )}
                  <input
                    type="file"
                    id="video-file-input"
                    accept="video/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="mt-3"
                    onClick={() => document.getElementById('video-file-input').click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Choose Video File
                  </Button>
                </div>
                
                {/* Alternative: Provide Video URL */}
                <div className="space-y-2">
                  <Label htmlFor="video-url">Or provide video URL (YouTube, Vimeo, etc.)</Label>
                  <Input
                    id="video-url"
                    value={videoForm.video_url}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, video_url: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                  />
                  <p className="text-xs text-gray-500">⚡ Recommended for videos larger than 10MB</p>
                </div>
                </div>
                
                {/* Buttons - sticky at bottom */}
                <div className="flex justify-end space-x-2 p-6 pt-4 border-t bg-white">
                  <Button type="button" variant="outline" onClick={() => {
                    setShowUploadDialog(false);
                    resetVideoForm();
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={!videoForm.title}
                  >
                    {editingVideoId ? 'Update Video' : 'Upload Video'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Video className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Videos</p>
                <p className="text-2xl font-bold text-gray-900">{videos.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-700" />
              <div>
                <p className="text-sm font-medium text-gray-600">Verified</p>
                <p className="text-2xl font-bold text-gray-900">
                  {videos.filter(v => v.verification_status === 'verified').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-yellow-700" />
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-gray-900">
                  {videos.filter(v => v.verification_status === 'pending').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Views</p>
                <p className="text-2xl font-bold text-gray-900">
                  {videos.reduce((sum, v) => sum + (v.view_count || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Video Gallery */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => (
          <Card key={video.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <CardTitle className="text-base line-clamp-2">{video.title}</CardTitle>
                    {getEditStatusBadge(video)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    by {video.uploaded_by_name} • {new Date(video.created_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
                <Badge className={getStatusColor(video.verification_status)}>
                  {video.verification_status}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="relative bg-gray-100 rounded-lg mb-3 aspect-video flex items-center justify-center">
                <Play className="h-12 w-12 text-gray-500" />
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                  {video.duration || '0:00'}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{video.description}</p>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span>{video.location || 'No location'}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Eye className="h-3 w-3" />
                  <span>{video.view_count || 0} views</span>
                </div>
              </div>

              {video.tags && video.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {video.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {video.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{video.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handlePlayVideo(video)}
                  disabled={!video.video_url}
                >
                  <Play className="h-3 w-3 mr-1" />
                  Play
                </Button>
                
                {user.role !== 'investor' && (
                  <div className="flex items-center space-x-1">
                    {/* Review button for PM/Admin when there are pending changes */}
                    {(video.edit_status === 'pending_approval' || video.edit_status === 'pending_deletion') && 
                     (user.role === 'project_manager' || user.role === 'administrator') && (
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          if (video.edit_status === 'pending_deletion') {
                            if (window.confirm(`Approve deletion of "${video.title}"?`)) {
                              handleApproveDeletion(video, true);
                            }
                          } else {
                            openApprovalDialog(video);
                          }
                        }}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Review
                      </Button>
                    )}
                    
                    {/* Edit/Delete buttons - hide when pending approval */}
                    {!video.edit_status && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-700 hover:text-blue-800"
                          onClick={() => handleEdit(video)}
                          title="Edit video"
                        >
                          <Edit3 className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDelete(video.id)}
                          title="Delete video"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {videos.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Video className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No videos uploaded yet</h3>
            <p className="text-gray-500 mb-4">
              Upload verification videos to document project progress and deliverables
            </p>
            {user.role !== 'investor' && (
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload First Video
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Universal Comments for Video Verification */}
      <UniversalComments 
        projectId={projectId} 
        user={user} 
        itemType="video_verification" 
        title="Video Verification Comments & Feedback" 
      />

      {/* Video Player Dialog */}
      <Dialog open={showVideoPlayer} onOpenChange={setShowVideoPlayer}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{playingVideo?.title || 'Video Player'}</DialogTitle>
          </DialogHeader>
          {playingVideo && playingVideo.video_url && (
            <div className="space-y-4">
              {(() => {
                const url = playingVideo.video_url;
                
                // Check if it's a YouTube URL
                if (url.includes('youtube.com') || url.includes('youtu.be')) {
                  let videoId = '';
                  if (url.includes('youtube.com/watch?v=')) {
                    videoId = url.split('v=')[1]?.split('&')[0];
                  } else if (url.includes('youtu.be/')) {
                    videoId = url.split('youtu.be/')[1]?.split('?')[0];
                  }
                  
                  return (
                    <iframe
                      className="w-full rounded-lg"
                      style={{ height: '60vh' }}
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={playingVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  );
                }
                
                // Check if it's a Vimeo URL
                if (url.includes('vimeo.com')) {
                  const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
                  return (
                    <iframe
                      className="w-full rounded-lg"
                      style={{ height: '60vh' }}
                      src={`https://player.vimeo.com/video/${videoId}`}
                      title={playingVideo.title}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  );
                }
                
                // For direct video files or Base64
                return (
                  <video
                    controls
                    className="w-full rounded-lg"
                    style={{ maxHeight: '70vh' }}
                    src={url}
                  >
                    Your browser does not support the video tag.
                  </video>
                );
              })()}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Uploaded by:</p>
                  <p className="font-medium">{playingVideo.uploaded_by_name}</p>
                </div>
                <div>
                  <p className="text-gray-600">Date:</p>
                  <p className="font-medium">{new Date(playingVideo.created_at).toLocaleString('en-GB')}</p>
                </div>
                <div>
                  <p className="text-gray-600">Type:</p>
                  <p className="font-medium">{playingVideo.verification_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-gray-600">Status:</p>
                  <Badge className={getStatusColor(playingVideo.verification_status)}>
                    {playingVideo.verification_status}
                  </Badge>
                </div>
              </div>
              {playingVideo.description && (
                <div>
                  <p className="text-gray-600 text-sm">Description:</p>
                  <p className="text-sm">{playingVideo.description}</p>
                </div>
              )}
              {playingVideo.location && (
                <div>
                  <p className="text-gray-600 text-sm">Location:</p>
                  <p className="text-sm">{playingVideo.location}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Video Changes</DialogTitle>
          </DialogHeader>
          
          {approvingVideo && approvingVideo.pending_changes && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-gray-700">Current Values</h4>
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-sm space-y-1">
                    {Object.entries(approvingVideo).map(([key, value]) => {
                      if (!['id', 'project_id', 'edit_status', 'pending_changes', '_id', 'created_at', 'updated_at', 'updated_by', 'video_file', 'thumbnail'].includes(key)) {
                        return (
                          <div key={key}>
                            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                            <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-gray-700">Proposed Changes</h4>
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-sm space-y-1">
                    {Object.entries(approvingVideo.pending_changes).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                        <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="approval-notes">Review Notes (Optional)</Label>
                <textarea
                  id="approval-notes"
                  className="w-full border rounded p-2 min-h-[80px]"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowApprovalDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => handleApprove(false)}
                >
                  Reject
                </Button>
                <Button
                  className="bg-green-700 hover:bg-green-800"
                  onClick={() => handleApprove(true)}
                >
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoVerification;